import type { CountryStats } from '@flightlog/shared';
import { useState } from 'react';
import { countryName } from '../countries';
import { plural } from '../format';
import { haptic } from '../telegram';
import { CONTINENTS } from '../world';
import { Flag } from './Flag';

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const TOTAL = CONTINENTS.reduce((sum, continent) => sum + continent.codes.length, 0);

/** «2018-08-27» → «авг 2018». */
function monthYear(date: string): string {
  const month = MONTHS[Number(date.slice(5, 7)) - 1];
  return month ? `${month} ${date.slice(0, 4)}` : date.slice(0, 4);
}

/** Паспорт: штампы стран с датой первого визита и все страны мира по частям света. */
export function Passport({ countries }: { countries: CountryStats[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const visited = new Map(countries.map((item) => [item.code.toUpperCase(), item]));
  const stamps = [...countries].sort((a, b) => a.firstDate.localeCompare(b.firstDate));
  const counted = CONTINENTS.reduce(
    (sum, continent) => sum + continent.codes.filter((code) => visited.has(code)).length,
    0,
  );

  return (
    <section className="section">
      <div className="section-head">
        <h2 className="section-title">Паспорт</h2>
        <span className="section-note">
          {counted} из {TOTAL} стран · {Math.round((counted / TOTAL) * 100)}% мира
        </span>
      </div>

      <div className="stamps">
        {stamps.map((item) => (
          <div className="stamp" key={item.code}>
            <Flag code={item.code} />
            <b>{countryName(item.code)}</b>
            <span>с {monthYear(item.firstDate)}</span>
            <span>
              {item.flights} {plural(item.flights, ['рейс', 'рейса', 'рейсов'])}
            </span>
          </div>
        ))}
      </div>

      <div className="continents">
        {CONTINENTS.map((continent) => {
          const seen = continent.codes.filter((code) => visited.has(code)).length;
          const isOpen = open === continent.name;
          return (
            <div className="continent" key={continent.name}>
              <button
                type="button"
                className="continent-head"
                aria-expanded={isOpen}
                onClick={() => {
                  haptic();
                  setOpen(isOpen ? null : continent.name);
                }}
              >
                <span className="continent-name">{continent.name}</span>
                <span className="continent-count">
                  {seen} из {continent.codes.length}
                </span>
                <span className="continent-track">
                  <i style={{ width: `${(seen / continent.codes.length) * 100}%` }} />
                </span>
                <span className={isOpen ? 'continent-arrow is-open' : 'continent-arrow'}>›</span>
              </button>
              {isOpen ? (
                <div className="continent-flags">
                  {[...continent.codes]
                    .sort((a, b) => Number(visited.has(b)) - Number(visited.has(a)))
                    .map((code) => (
                      <div className={visited.has(code) ? 'world-flag' : 'world-flag is-new'} key={code}>
                        <Flag code={code} lazy />
                        <span>{countryName(code)}</span>
                      </div>
                    ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
